import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { LinkIcon, InformationCircleIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, AppLoader, ConfirmDialog } from '../../components/ui';
import { integrationApi } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { IntegrationPlatform, IntegrationRecord } from '../../types/integration';

// ---- Integration config ----
interface IntegrationConfig {
	id: string;
	platform: IntegrationPlatform;
	name: string;
	shortName: string;
	description: string;
	urlPlaceholder: string;
	urlHint: string;
	logoClass: string;
	features: string[];
}

const INTEGRATIONS: IntegrationConfig[] = [
	{
		id: 'trendit',
		platform: 'TRENDIT',
		name: 'Trendit',
		shortName: 'T',
		description:
			'Connect your Trendit account to automatically sync appointments, customer information, and booking details.',
		urlPlaceholder: 'https://trenditapp.com/booking/your-business',
		urlHint: 'Enter your Trendit booking page URL',
		logoClass: 'integration-card__logo--trendit',
		features: [
			'Auto-sync new bookings',
			'Import customer details',
			'Real-time availability updates',
		],
	},
	{
		id: 'b4b',
		platform: 'BOOK4BRAIDS',
		name: 'BookForBraids',
		shortName: 'B4B',
		description:
			'Connect your BookForBraids account to sync braiding appointments and manage bookings from one place.',
		urlPlaceholder: 'https://bookforbraids.com/provider/your-business',
		urlHint: 'Enter your BookForBraids provider page URL',
		logoClass: 'integration-card__logo--b4b',
		features: ['Sync braiding appointments', 'Import service details', 'Unified calendar view'],
	},
];

// ---- Integration Card ----
interface IntegrationCardProps {
	config: IntegrationConfig;
	records: IntegrationRecord[];
	locationId: number;
	onChanged: () => void;
}

const IntegrationCard = ({ config, records, locationId, onChanged }: IntegrationCardProps) => {
	const [url, setUrl] = useState('');
	const [isConnecting, setIsConnecting] = useState(false);
	const [removingId, setRemovingId] = useState<number | null>(null);
	const [showForm, setShowForm] = useState(false);
	const [removeRecord, setRemoveRecord] = useState<IntegrationRecord | null>(null);

	const connectedCount = records.length;
	const isConnected = connectedCount > 0;

	const handleConnect = async () => {
		if (!url.trim()) {
			toast.error(`Please enter your ${config.name} URL`);
			return;
		}

		setIsConnecting(true);
		try {
			await integrationApi.create({
				locationId,
				platform: config.platform,
				profileUrl: url.trim(),
			});
			toast.success(`${config.name} connected successfully!`);
			setUrl('');
			setShowForm(false);
			onChanged();
		} catch (error) {
			const detail =
				error instanceof Error && error.message ? error.message : 'Please try again.';
			toast.error(`Failed to connect ${config.name}. ${detail}`);
		} finally {
			setIsConnecting(false);
		}
	};

	const handleRemove = async () => {
		if (!removeRecord) return;
		setRemovingId(removeRecord.id);
		try {
			await integrationApi.remove(removeRecord.id);
			toast.success(`Account removed successfully`);
			onChanged();
		} catch {
			toast.error(`Failed to remove account. Please try again.`);
		} finally {
			setRemovingId(null);
			setRemoveRecord(null);
		}
	};

	return (
		<div className={`integration-card ${isConnected ? 'integration-card--connected' : ''}`}>
			{/* Header */}
			<div className="integration-card__header">
				<div className={`integration-card__logo ${config.logoClass}`}>
					{config.shortName}
				</div>
				<div className="integration-card__info">
					<h3 className="integration-card__name">{config.name}</h3>
					<p className="integration-card__type">Booking Platform</p>
				</div>
				<div className="integration-card__status">
					{isConnected ? (
						<Badge variant="success">{connectedCount} Connected</Badge>
					) : (
						<Badge variant="info">Not Connected</Badge>
					)}
				</div>
			</div>

			{/* Body */}
			<div className="integration-card__body">
				<p className="integration-card__description">{config.description}</p>

				<ul className="integration-card__features">
					{config.features.map(feature => (
						<li key={feature} className="integration-card__feature">
							<CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
							<span>{feature}</span>
						</li>
					))}
				</ul>

				{/* Connected Accounts List */}
				{isConnected && (
					<div className="connected-urls-section">
						<div className="connected-urls-section__title">
							Connected Accounts
							<span className="connected-urls-section__count">{connectedCount}</span>
						</div>
						{records.map(rec => (
							<div key={rec.id} className="connected-url-item">
								<div className="connected-url-item__info">
									<div className="connected-url-item__name">
										{rec.externalProviderName || rec.externalSlug}
									</div>
									<div className="connected-url-item__value">
										{rec.externalSlug}
									</div>
									<div className="connected-url-item__date">
										Connected {format(new Date(rec.createdAt), 'MMM d, yyyy')}
									</div>
								</div>
								<button
									className="connected-url-item__remove"
									onClick={() => setRemoveRecord(rec)}
									disabled={removingId === rec.id}
									title="Remove"
								>
									{removingId === rec.id ? (
										<span className="w-4 h-4 block border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
									) : (
										<XMarkIcon className="w-4 h-4" />
									)}
								</button>
							</div>
						))}
					</div>
				)}

				{/* Connection Form */}
				{(!isConnected || showForm) && (
					<div className="integration-card__form">
						<div className="provider-form__group">
							<label className="provider-form__label">{config.name} URL</label>
							<input
								type="url"
								className="provider-form__input"
								placeholder={config.urlPlaceholder}
								value={url}
								onChange={e => setUrl(e.target.value)}
								disabled={isConnecting}
							/>
							<span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
								{config.urlHint}
							</span>
						</div>
						<Button
							variant="primary"
							className="w-full"
							onClick={handleConnect}
							isLoading={isConnecting}
						>
							Connect {config.name}
						</Button>
					</div>
				)}

				{/* Add Another Button */}
				{isConnected && !showForm && (
					<button
						className="integration-card__add-another"
						onClick={() => setShowForm(true)}
					>
						<PlusIcon className="w-4 h-4" />
						<span>Add Another {config.name} URL</span>
					</button>
				)}
			</div>

			{/* Remove Confirmation Dialog */}
			<ConfirmDialog
				isOpen={removeRecord !== null}
				onClose={() => setRemoveRecord(null)}
				onConfirm={handleRemove}
				title="Remove Account"
				message={`Are you sure you want to remove "${removeRecord?.externalProviderName || removeRecord?.externalSlug || ''}" from ${config.name}?`}
				confirmLabel="Yes, Remove"
				variant="danger"
				isLoading={removingId !== null}
			/>
		</div>
	);
};

// ---- Main Integrations Page ----
const Integrations = () => {
	const { selectedLocationId } = useProviderProfileStore();
	const [records, setRecords] = useState<IntegrationRecord[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const fetchIntegrations = useCallback(async () => {
		if (!selectedLocationId) return;
		try {
			const data = await integrationApi.list(selectedLocationId);
			setRecords(data);
		} catch {
			toast.error('Failed to load integrations.');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId]);

	useEffect(() => {
		setIsLoading(true);
		fetchIntegrations();
	}, [fetchIntegrations]);

	const getRecordsForPlatform = (platform: IntegrationPlatform): IntegrationRecord[] => {
		return records.filter(r => r.platform.toUpperCase() === platform.toUpperCase());
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<div className="flex items-center gap-3 mb-1">
					<LinkIcon className="w-6 h-6 text-gray-400" />
					<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
						Integrations
					</h1>
				</div>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					Connect your booking platforms to sync appointments automatically
				</p>
			</div>

			{/* Integration Cards Grid */}
			{isLoading ? (
				<div className="flex items-center justify-center py-16">
					<AppLoader message="Loading integrations" variant="inline" />
				</div>
			) : (
				<div className="integrations-grid">
					{INTEGRATIONS.map(config => (
						<IntegrationCard
							key={config.id}
							config={config}
							records={getRecordsForPlatform(config.platform)}
							locationId={selectedLocationId!}
							onChanged={fetchIntegrations}
						/>
					))}
				</div>
			)}

			{/* Info Card */}
			<div className="integrations-info-card">
				<InformationCircleIcon className="w-8 h-8 text-blue-500 dark:text-blue-400 shrink-0" />
				<div>
					<h4 className="integrations-info-card__title">
						Why Connect Your Booking Platforms?
					</h4>
					<p className="integrations-info-card__text">
						By connecting Trendit and BookForBraids, all your appointments will
						automatically appear in BBP. This means you can manage staff schedules,
						track commissions, and view analytics for all bookings in one place — no
						manual entry required!
					</p>
				</div>
			</div>
		</AppLayout>
	);
};

export default Integrations;
